/**
 * Cross-tab preference sync channel.
 *
 * Uses the browser BroadcastChannel API so that when a user changes a
 * preference in one tab (Settings page, theme toggle, etc.), every other
 * open tab in the same origin picks up the change immediately without
 * needing a page refresh.
 *
 * Message shape:
 *   { type: 'PREFERENCE_UPDATE', key: <preference-name>, value: <new-value> }
 *
 * Consumers should:
 *   - call `broadcastPreferenceUpdate(key, value)` right AFTER a successful
 *     save (server confirmed) so we don't advertise a change that never
 *     persisted.
 *   - call `subscribeToPreferences(handler)` inside a useEffect and return
 *     the cleanup fn — it closes the listener so we don't leak.
 *
 * SSR / older browser safe: falls back to no-op if BroadcastChannel is
 * unavailable (e.g., older Safari without the API).
 */

const CHANNEL_NAME = 'user-preferences';
const isSupported = typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function';

// Single shared channel per tab. Kept open for the tab's entire lifetime;
// individual listeners are the ones that come and go.
let sharedChannel = null;
const getChannel = () => {
  if (!isSupported) return null;
  if (!sharedChannel) sharedChannel = new BroadcastChannel(CHANNEL_NAME);
  return sharedChannel;
};

/** Send a preference update to every OTHER tab in this origin. */
export const broadcastPreferenceUpdate = (key, value) => {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: 'PREFERENCE_UPDATE', key, value });
  } catch (err) {
    // postMessage can throw on non-cloneable values; keep the UI going.
    console.warn('[preferences] broadcast failed', err);
  }
};

/**
 * Subscribe to PREFERENCE_UPDATE messages from other tabs. Returns an
 * unsubscribe function suitable for a useEffect cleanup return.
 *
 * `handler({ key, value })` is only invoked for well-formed messages —
 * anything else is silently dropped.
 */
export const subscribeToPreferences = (handler) => {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event) => {
    const msg = event?.data;
    if (!msg || msg.type !== 'PREFERENCE_UPDATE' || typeof msg.key !== 'string') return;
    try {
      handler({ key: msg.key, value: msg.value });
    } catch (err) {
      console.error('[preferences] handler threw', err);
    }
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
};
